import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

/**
 * OperatingAgreementForm — intake form for the OA Business and FLP families.
 *
 * Produces an `operating-agreement-import/v1` JSON payload via onChange.
 * Parent passes the payload to the OA import mapper; the mapper validates,
 * resolves the package, and produces the flat variables dict that docxtpl
 * renders into oa-{business|flp}.docx.
 *
 * Controlled component: parent owns state. On every field change this
 * component emits the full intake JSON, which the parent can either feed
 * directly to the mapper or post to the document orchestration API.
 *
 * Deliberately separate from KeyPeopleManager — the OA domain (members,
 * managers, units, tax classification, buy-sell triggers) is disjoint from
 * the fiduciary-role domain that drives estate planning.
 */

const PACKAGE_OPTIONS = [
  { value: 'Operating Agreement Package - Business', label: 'Business-style' },
  { value: 'Operating Agreement Package - FLP', label: 'FLP-style (preview-only)' },
];

const TAX_OPTIONS = [
  { value: 'disregarded', label: 'Disregarded entity' },
  { value: 'partnership', label: 'Partnership' },
  { value: 's_corp', label: 'S corporation' },
  { value: 'c_corp', label: 'C corporation' },
];

const MANAGEMENT_MODE_OPTIONS = [
  { value: 'manager_managed', label: 'Manager-managed' },
  { value: 'member_managed', label: 'Member-managed' },
];

const TIEBREAKER_OPTIONS = [
  { value: 'majority_member', label: 'Majority Member breaks ties' },
  { value: 'named_tiebreaker', label: 'Named tiebreaker' },
  { value: 'mediation_arbitration', label: 'Mediation / arbitration' },
];

const DEATH_OPTIONS = [
  { value: 'mandatory_cross_purchase', label: 'Mandatory cross-purchase' },
  { value: 'optional', label: 'Optional' },
  { value: 'redemption', label: 'Company redemption' },
];

const DEPARTURE_OPTIONS = [
  { value: 'optional', label: 'Optional' },
  { value: 'mandatory', label: 'Mandatory' },
];

const INSURANCE_OPTIONS = [
  { value: 'mandate', label: 'Mandate' },
  { value: 'recommend', label: 'Recommend' },
  { value: 'silent', label: 'Silent' },
];

const CAPITAL_CALL_OPTIONS = [
  { value: 'permitted', label: 'Permitted' },
  { value: 'mandatory', label: 'Mandatory' },
  { value: 'prohibited', label: 'Prohibited' },
];

const APPRAISER_OPTIONS = [
  { value: 'company', label: 'Company selects' },
  { value: 'joint', label: 'Joint selection' },
  { value: 'third_party', label: 'Third-party panel' },
];

const TRANSFER_SCOPE_OPTIONS = [
  { value: 'rlt_only', label: 'Revocable living trust only' },
  { value: 'standard', label: 'RLT + family + qualifying trusts' },
  { value: 'narrow_descendants', label: 'Descendants only' },
  { value: 'custom', label: 'Custom definition' },
];

const DISPUTE_OPTIONS = [
  { value: 'mediation_first_then_arb', label: 'Mediation then arbitration' },
  { value: 'arbitration', label: 'Arbitration only' },
  { value: 'litigation', label: 'Litigation' },
];

const ARBITRATION_PROVIDERS = ['AAA', 'JAMS'];

function emptyMember() {
  return {
    full_name: '',
    address: '',
    units_class_a: null,
    units_class_b: null,
    capital_contribution: '',
    percentage_interest: 0,
    is_manager: false,
    spouse_joinder_required: false,
  };
}

function defaultsFor(packageType) {
  const flp = packageType === 'Operating Agreement Package - FLP';
  return {
    class_structure: {
      has_ab_classes: flp,
      class_a_definition: flp ? 'General Partner / Class A Units (voting)' : null,
      class_b_definition: flp ? 'Limited Partner / Class B Units (non-voting)' : null,
    },
    transferees: { scope: flp ? 'narrow_descendants' : 'rlt_only' },
    buy_sell: {
      death_buyout_mode: 'mandatory_cross_purchase',
      disability_days: 180,
      disability_reduction_pct: 50,
      departure_voluntary_mode: 'optional',
      departure_involuntary_mode: 'mandatory',
      involuntary_reduction_pct: 50,
      insurance_funding: 'recommend',
    },
    pricing: { covered_period_default_months: 12, covered_period_max_months: 24, appraiser_selection: 'company' },
    payment: { note_term_months: 60, interest_rate_expression: 'prime rate plus 1.0%', rofr_acceptance_days: 30, rofr_sale_window_days: 90 },
    distributions: { tax_distribution_mandatory: true, capital_calls_mode: 'permitted' },
    management: { mode: 'manager_managed', veto_threshold_pct: 50, asset_sale_threshold_amount: 100000, manager_tiebreaker: 'majority_member', majority_can_remove_co_manager: true },
  };
}

export default function OperatingAgreementForm({ initialPackage = 'Operating Agreement Package - Business', onChange, onSubmit }) {
  const [packageType, setPackageType] = useState(initialPackage);
  const [engagement, setEngagement] = useState({ matter_id: '', engagement_date: '', drafting_attorney: '', paralegal_name: '' });
  const [company, setCompany] = useState({ name: '', state_of_formation: '', principal_office: '', purpose: '', effective_date: '', ein: '', fiscal_year_end: 'December 31' });
  const [tax, setTax] = useState({ classification: 'partnership' });
  const [classStructure, setClassStructure] = useState(defaultsFor(initialPackage).class_structure);
  const [members, setMembers] = useState([emptyMember(), emptyMember()]);
  const [management, setManagement] = useState(defaultsFor(initialPackage).management);
  const [buySell, setBuySell] = useState(defaultsFor(initialPackage).buy_sell);
  const [pricing, setPricing] = useState(defaultsFor(initialPackage).pricing);
  const [payment, setPayment] = useState(defaultsFor(initialPackage).payment);
  const [transferees, setTransferees] = useState({ scope: defaultsFor(initialPackage).transferees.scope, custom_definition: '' });
  const [distributions, setDistributions] = useState(defaultsFor(initialPackage).distributions);
  const [disputes, setDisputes] = useState({ forum: 'mediation_first_then_arb', governing_law_state: '', arbitration_provider: 'AAA', venue_county: '' });
  const [representation, setRepresentation] = useState({ represented_member_name: '', represents_entity: false, conflict_waiver_attached: true });
  const [deliverables, setDeliverables] = useState({ include_summary_memo: true, include_schedule_c: true });

  const isFlp = packageType === 'Operating Agreement Package - FLP';

  const intake = useMemo(() => ({
    schema_name: 'operating-agreement-import',
    schema_version: '1.0',
    package_candidate: packageType,
    engagement,
    company,
    tax,
    class_structure: classStructure,
    members: members.filter(m => m.full_name.trim()),
    management,
    buy_sell: buySell,
    pricing,
    payment,
    transferees,
    distributions,
    disputes: {
      ...disputes,
      governing_law_state: disputes.governing_law_state || company.state_of_formation || null,
    },
    representation,
    deliverables,
    ambiguities: [],
  }), [packageType, engagement, company, tax, classStructure, members, management, buySell, pricing, payment, transferees, distributions, disputes, representation, deliverables]);

  useEffect(() => {
    if (onChange) onChange(intake);
  }, [intake, onChange]);

  const handlePackageChange = useCallback((newPkg) => {
    setPackageType(newPkg);
    const d = defaultsFor(newPkg);
    setClassStructure(d.class_structure);
    setTransferees(prev => ({ ...prev, scope: d.transferees.scope }));
  }, []);

  const updateMember = useCallback((idx, patch) => {
    setMembers(prev => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  }, []);

  const addMember = useCallback(() => setMembers(prev => [...prev, emptyMember()]), []);
  const removeMember = useCallback((idx) => setMembers(prev => prev.filter((_, i) => i !== idx)), []);

  const handleSubmit = useCallback(() => {
    if (onSubmit) onSubmit(intake);
  }, [intake, onSubmit]);

  // Validation summary
  const issues = useMemo(() => {
    const out = [];
    if (!company.name.trim()) out.push('Company name is required.');
    if (!company.state_of_formation.trim()) out.push('State of formation is required.');
    if (members.filter(m => m.full_name.trim()).length === 0) out.push('At least one member is required.');
    const totalPct = members.reduce((s, m) => s + (Number(m.percentage_interest) || 0), 0);
    if (totalPct > 0 && Math.abs(totalPct - 100) > 0.01) out.push(`Member percentages total ${totalPct}% (must equal 100%).`);
    return out;
  }, [company, members]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Operating Agreement Intake</Typography>

      {isFlp && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          FLP template is preview-quality. Attorney must review every page before delivery.
        </Alert>
      )}

      <TextField
        select fullWidth size="small" label="Package" value={packageType}
        onChange={(e) => handlePackageChange(e.target.value)} sx={{ mb: 2 }}
      >
        {PACKAGE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>

      <Section title="1. Engagement & Company" defaultExpanded>
        <Stack spacing={1.5}>
          <Row>
            <TextField size="small" label="Matter ID" value={engagement.matter_id}
              onChange={(e) => setEngagement({ ...engagement, matter_id: e.target.value })} fullWidth />
            <TextField size="small" label="Engagement date" value={engagement.engagement_date}
              onChange={(e) => setEngagement({ ...engagement, engagement_date: e.target.value })}
              placeholder="e.g. April 1, 2026" fullWidth />
          </Row>
          <Row>
            <TextField size="small" label="Drafting attorney" value={engagement.drafting_attorney}
              onChange={(e) => setEngagement({ ...engagement, drafting_attorney: e.target.value })} fullWidth />
            <TextField size="small" label="Paralegal" value={engagement.paralegal_name || ''}
              onChange={(e) => setEngagement({ ...engagement, paralegal_name: e.target.value })} fullWidth />
          </Row>
          <Divider />
          <TextField size="small" label="Company legal name (with entity suffix)" value={company.name}
            onChange={(e) => setCompany({ ...company, name: e.target.value })} fullWidth required />
          <Row>
            <TextField size="small" label="State of formation" value={company.state_of_formation}
              onChange={(e) => setCompany({ ...company, state_of_formation: e.target.value })} fullWidth required />
            <TextField size="small" label="Effective date" value={company.effective_date}
              onChange={(e) => setCompany({ ...company, effective_date: e.target.value })}
              placeholder="e.g. April 1, 2026" fullWidth />
          </Row>
          <TextField size="small" label="Principal office address (one line)" value={company.principal_office}
            onChange={(e) => setCompany({ ...company, principal_office: e.target.value })} fullWidth />
          <TextField size="small" label="Purpose (one-sentence statement)" value={company.purpose}
            onChange={(e) => setCompany({ ...company, purpose: e.target.value })} fullWidth multiline minRows={2} />
          <Row>
            <TextField size="small" label="EIN" value={company.ein || ''}
              onChange={(e) => setCompany({ ...company, ein: e.target.value })} fullWidth />
            <TextField size="small" label="Fiscal year end" value={company.fiscal_year_end || ''}
              onChange={(e) => setCompany({ ...company, fiscal_year_end: e.target.value })} fullWidth />
          </Row>
        </Stack>
      </Section>

      <Section title="2. Tax classification">
        <TextField select size="small" label="Tax classification" value={tax.classification}
          onChange={(e) => setTax({ classification: e.target.value })} fullWidth>
          {TAX_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
      </Section>

      <Section title="3. Class structure">
        <FormControlLabel
          control={<Switch checked={!!classStructure.has_ab_classes}
            onChange={(e) => setClassStructure({ ...classStructure, has_ab_classes: e.target.checked })} />}
          label="Two classes of units (A / B)"
        />
        {classStructure.has_ab_classes && (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <TextField size="small" label="Class A definition" value={classStructure.class_a_definition || ''}
              onChange={(e) => setClassStructure({ ...classStructure, class_a_definition: e.target.value })} fullWidth />
            <TextField size="small" label="Class B definition" value={classStructure.class_b_definition || ''}
              onChange={(e) => setClassStructure({ ...classStructure, class_b_definition: e.target.value })} fullWidth />
          </Stack>
        )}
      </Section>

      <Section title="4. Members">
        <Stack spacing={2}>
          {members.map((m, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={1.25}>
                <Row>
                  <TextField size="small" label={`Member ${idx + 1} full name`} value={m.full_name}
                    onChange={(e) => updateMember(idx, { full_name: e.target.value })} fullWidth />
                  <TextField size="small" type="number" label="% interest" value={m.percentage_interest}
                    onChange={(e) => updateMember(idx, { percentage_interest: Number(e.target.value) })}
                    inputProps={{ min: 0, max: 100, step: 0.01 }} sx={{ width: 140 }} />
                  <IconButton onClick={() => removeMember(idx)} aria-label="remove member" size="small">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Row>
                <TextField size="small" label="Address" value={m.address || ''}
                  onChange={(e) => updateMember(idx, { address: e.target.value })} fullWidth />
                <TextField size="small" label="Initial capital contribution" value={m.capital_contribution || ''}
                  onChange={(e) => updateMember(idx, { capital_contribution: e.target.value })} fullWidth
                  placeholder="Cash amount or property description" />
                <Row>
                  <FormControlLabel
                    control={<Switch checked={!!m.is_manager} onChange={(e) => updateMember(idx, { is_manager: e.target.checked })} />}
                    label="Co-Manager"
                  />
                  <FormControlLabel
                    control={<Switch checked={!!m.spouse_joinder_required} onChange={(e) => updateMember(idx, { spouse_joinder_required: e.target.checked })} />}
                    label="Spouse joinder required"
                  />
                </Row>
                {classStructure.has_ab_classes && (
                  <Row>
                    <TextField size="small" type="number" label="Class A units" value={m.units_class_a ?? ''}
                      onChange={(e) => updateMember(idx, { units_class_a: e.target.value === '' ? null : Number(e.target.value) })} fullWidth />
                    <TextField size="small" type="number" label="Class B units" value={m.units_class_b ?? ''}
                      onChange={(e) => updateMember(idx, { units_class_b: e.target.value === '' ? null : Number(e.target.value) })} fullWidth />
                  </Row>
                )}
              </Stack>
            </Paper>
          ))}
          <Button onClick={addMember} startIcon={<AddIcon />} size="small" variant="outlined">
            Add member
          </Button>
        </Stack>
      </Section>

      <Section title="5. Management">
        <Stack spacing={1.5}>
          <TextField select size="small" label="Management mode" value={management.mode}
            onChange={(e) => setManagement({ ...management, mode: e.target.value })} fullWidth>
            {MANAGEMENT_MODE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <Row>
            <TextField size="small" type="number" label="Member-veto threshold (%)" value={management.veto_threshold_pct}
              onChange={(e) => setManagement({ ...management, veto_threshold_pct: Number(e.target.value) })}
              inputProps={{ min: 50, max: 100 }} fullWidth />
            <TextField size="small" type="number" label="Asset-sale Member-consent threshold ($)" value={management.asset_sale_threshold_amount}
              onChange={(e) => setManagement({ ...management, asset_sale_threshold_amount: Number(e.target.value) })} fullWidth />
          </Row>
          <TextField select size="small" label="Manager tie-breaker" value={management.manager_tiebreaker}
            onChange={(e) => setManagement({ ...management, manager_tiebreaker: e.target.value })} fullWidth>
            {TIEBREAKER_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={<Switch checked={!!management.majority_can_remove_co_manager}
              onChange={(e) => setManagement({ ...management, majority_can_remove_co_manager: e.target.checked })} />}
            label="Majority Member can remove a Co-Manager"
          />
        </Stack>
      </Section>

      <Section title="6. Buy-sell — The Four Ds">
        <Stack spacing={1.5}>
          <Row>
            <TextField select size="small" label="Death buyout" value={buySell.death_buyout_mode}
              onChange={(e) => setBuySell({ ...buySell, death_buyout_mode: e.target.value })} fullWidth>
              {DEATH_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Insurance funding" value={buySell.insurance_funding}
              onChange={(e) => setBuySell({ ...buySell, insurance_funding: e.target.value })} fullWidth>
              {INSURANCE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          </Row>
          <Row>
            <TextField size="small" type="number" label="Disability days" value={buySell.disability_days}
              onChange={(e) => setBuySell({ ...buySell, disability_days: Number(e.target.value) })} fullWidth />
            <TextField size="small" type="number" label="Disability reduction (%)" value={buySell.disability_reduction_pct}
              onChange={(e) => setBuySell({ ...buySell, disability_reduction_pct: Number(e.target.value) })} fullWidth />
          </Row>
          <Row>
            <TextField select size="small" label="Voluntary departure" value={buySell.departure_voluntary_mode}
              onChange={(e) => setBuySell({ ...buySell, departure_voluntary_mode: e.target.value })} fullWidth>
              {DEPARTURE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Involuntary events" value={buySell.departure_involuntary_mode}
              onChange={(e) => setBuySell({ ...buySell, departure_involuntary_mode: e.target.value })} fullWidth>
              {DEPARTURE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField size="small" type="number" label="Involuntary reduction (%)" value={buySell.involuntary_reduction_pct}
              onChange={(e) => setBuySell({ ...buySell, involuntary_reduction_pct: Number(e.target.value) })} sx={{ width: 180 }} />
          </Row>
        </Stack>
      </Section>

      <Section title="7. Pricing & Payment">
        <Stack spacing={1.5}>
          <Row>
            <TextField size="small" type="number" label="Covered Period default (months)" value={pricing.covered_period_default_months}
              onChange={(e) => setPricing({ ...pricing, covered_period_default_months: Number(e.target.value) })} fullWidth />
            <TextField size="small" type="number" label="Covered Period max (months)" value={pricing.covered_period_max_months}
              onChange={(e) => setPricing({ ...pricing, covered_period_max_months: Number(e.target.value) })} fullWidth />
          </Row>
          <TextField select size="small" label="Appraiser selection" value={pricing.appraiser_selection}
            onChange={(e) => setPricing({ ...pricing, appraiser_selection: e.target.value })} fullWidth>
            {APPRAISER_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <Divider />
          <Row>
            <TextField size="small" type="number" label="Note term (months)" value={payment.note_term_months}
              onChange={(e) => setPayment({ ...payment, note_term_months: Number(e.target.value) })} fullWidth />
            <TextField size="small" label="Interest rate expression" value={payment.interest_rate_expression}
              onChange={(e) => setPayment({ ...payment, interest_rate_expression: e.target.value })} fullWidth />
          </Row>
          <Row>
            <TextField size="small" type="number" label="ROFR acceptance window (days)" value={payment.rofr_acceptance_days}
              onChange={(e) => setPayment({ ...payment, rofr_acceptance_days: Number(e.target.value) })} fullWidth />
            <TextField size="small" type="number" label="ROFR sale window (days)" value={payment.rofr_sale_window_days}
              onChange={(e) => setPayment({ ...payment, rofr_sale_window_days: Number(e.target.value) })} fullWidth />
          </Row>
        </Stack>
      </Section>

      <Section title="8. Transferees & Distributions">
        <Stack spacing={1.5}>
          <TextField select size="small" label="Permitted transferees" value={transferees.scope}
            onChange={(e) => setTransferees({ ...transferees, scope: e.target.value })} fullWidth>
            {TRANSFER_SCOPE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          {transferees.scope === 'custom' && (
            <TextField size="small" multiline minRows={2} label="Custom permitted-transferee definition"
              value={transferees.custom_definition || ''}
              onChange={(e) => setTransferees({ ...transferees, custom_definition: e.target.value })} fullWidth />
          )}
          <Divider />
          <FormControlLabel
            control={<Switch checked={!!distributions.tax_distribution_mandatory}
              onChange={(e) => setDistributions({ ...distributions, tax_distribution_mandatory: e.target.checked })} />}
            label="Mandatory tax distributions"
          />
          <TextField select size="small" label="Capital calls" value={distributions.capital_calls_mode}
            onChange={(e) => setDistributions({ ...distributions, capital_calls_mode: e.target.value })} fullWidth>
            {CAPITAL_CALL_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        </Stack>
      </Section>

      <Section title="9. Disputes & Representation">
        <Stack spacing={1.5}>
          <Row>
            <TextField select size="small" label="Dispute forum" value={disputes.forum}
              onChange={(e) => setDisputes({ ...disputes, forum: e.target.value })} fullWidth>
              {DISPUTE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Arbitration provider" value={disputes.arbitration_provider || ''}
              onChange={(e) => setDisputes({ ...disputes, arbitration_provider: e.target.value })} fullWidth>
              {ARBITRATION_PROVIDERS.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          </Row>
          <Row>
            <TextField size="small" label="Governing law state (defaults to state of formation)"
              value={disputes.governing_law_state || ''}
              onChange={(e) => setDisputes({ ...disputes, governing_law_state: e.target.value })} fullWidth />
            <TextField size="small" label="Venue county" value={disputes.venue_county || ''}
              onChange={(e) => setDisputes({ ...disputes, venue_county: e.target.value })} fullWidth />
          </Row>
          <Divider />
          <TextField size="small" label="Member represented by Stewart Law" value={representation.represented_member_name || ''}
            onChange={(e) => setRepresentation({ ...representation, represented_member_name: e.target.value })} fullWidth />
          <Row>
            <FormControlLabel
              control={<Switch checked={!!representation.represents_entity}
                onChange={(e) => setRepresentation({ ...representation, represents_entity: e.target.checked })} />}
              label="Firm also represents the Company"
            />
            <FormControlLabel
              control={<Switch checked={!!representation.conflict_waiver_attached}
                onChange={(e) => setRepresentation({ ...representation, conflict_waiver_attached: e.target.checked })} />}
              label="Conflict waiver attached"
            />
          </Row>
        </Stack>
      </Section>

      <Section title="10. Deliverables">
        <Stack spacing={1}>
          <FormControlLabel
            control={<Switch checked={!!deliverables.include_summary_memo}
              onChange={(e) => setDeliverables({ ...deliverables, include_summary_memo: e.target.checked })} />}
            label="Generate one-page summary memo"
          />
          <FormControlLabel
            control={<Switch checked={!!deliverables.include_schedule_c}
              onChange={(e) => setDeliverables({ ...deliverables, include_schedule_c: e.target.checked })} />}
            label="Generate first-year Schedule C (Agreed Price Statement)"
          />
        </Stack>
      </Section>

      {issues.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight={500}>Resolve before generation:</Typography>
          <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
            {issues.map((i, ix) => <li key={ix}><Typography variant="caption">{i}</Typography></li>)}
          </ul>
        </Alert>
      )}

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleSubmit} disabled={issues.length > 0}>
          Generate Operating Agreement
        </Button>
      </Box>
    </Paper>
  );
}

function Section({ title, defaultExpanded = false, children }) {
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight={500}>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

function Row({ children }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', '& > *': { mt: { xs: 0.5, sm: 0 } } }}>
      {children}
    </Stack>
  );
}
